import { create } from 'zustand';

// Model runs from this browser (2026-10-22). Every button that calls a model
// (stage Generate, field Regenerate, Add another fake, Re-run scoring, Add
// word scoring) goes through trackRun, so the top bar, the stage button, the
// stage strip and the Word Binder all read the same list. The list lives
// outside any page, so a run stays visible after switching tabs or words, and
// its pop-up shows wherever Odin is when it ends.

export type RunKind = 'generate' | 'regenerate' | 'fake' | 'score';

export interface Run {
  id: number;
  kind: RunKind;
  // The word's id, or null while Add word has not saved it yet.
  wordId: string | null;
  word: string;
  // "Stage 1", "Regenerate Etymology", "Scoring".
  label: string;
  // Content stage id for kind 'generate' (1, 1b, rv...), the field for 'regenerate'.
  stage?: string;
  field?: string;
  startedAt: number;
}

export interface RunToast {
  id: number;
  ok: boolean;
  text: string;
}

const TOAST_MS = 8000;

interface RunState {
  runs: Run[];
  toasts: RunToast[];
  // Bumped each time a run on a word ends, so an open Content page reloads it.
  finished: Record<string, number>;
  dismiss: (id: number) => void;
}

export const useRunStore = create<RunState>()((set) => ({
  runs: [],
  toasts: [],
  finished: {},
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

let nextId = 1;

function errorOf(e: unknown): string {
  const err = e as { data?: { error?: string }; message?: string };
  return err?.data?.error || err?.message || 'Something went wrong.';
}

/**
 * Run one model call and show it everywhere while it runs. The call's result
 * or error passes straight through, so the caller's own handling is unchanged.
 * `doneText` may name the finished word when the reply knows it better.
 */
export async function trackRun<T>(
  meta: Omit<Run, 'id' | 'startedAt'>,
  call: () => Promise<T>,
  doneText?: (r: T) => string,
): Promise<T> {
  const run: Run = { ...meta, id: nextId++, startedAt: Date.now() };
  useRunStore.setState((s) => ({ runs: [...s.runs, run] }));
  let toast: RunToast;
  try {
    const r = await call();
    toast = { id: run.id, ok: true, text: doneText ? doneText(r) : `${run.label} done: ${run.word}` };
    return r;
  } catch (e) {
    toast = { id: run.id, ok: false, text: `${run.label} failed: ${run.word}. ${errorOf(e)}` };
    throw e;
  } finally {
    useRunStore.setState((s) => ({
      runs: s.runs.filter((x) => x.id !== run.id),
      toasts: [...s.toasts, toast],
      finished: run.wordId ? { ...s.finished, [run.wordId]: Date.now() } : s.finished,
    }));
    window.setTimeout(() => useRunStore.getState().dismiss(run.id), TOAST_MS);
  }
}

/** "0:14", "1:03". */
export function elapsedText(startedAt: number, now: number): string {
  const s = Math.max(0, Math.floor((now - startedAt) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
